import { useId, useRef, useState } from 'react'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { Button } from '@/components/button/Button'

export default function CreateTableDialog({ onContinue, onClose }: { onContinue: (fromFiles: boolean) => Promise<void>; onClose: () => void }) {
  const formId = useId()
  const [fromFiles, setFromFiles] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submitting = useRef(false)
  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose()
      }}
    >
      <ModalContent
        title="Create a table"
        width="xl"
        height="max-lg"
        className="max-w-[734px]"
        footer={
          <div className="flex flex-col gap-4">
            {error && (
              <p role="alert" className="text-sm text-red-800">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-3.5">
              <Button type="button" variant="outlined" disabled={saving} onClick={onClose}>
                Cancel
              </Button>
              <Button form={formId} type="submit" disabled={saving} loading={saving}>
                Continue
              </Button>
            </div>
          </div>
        }
        preventDismiss={saving}
      >
        <form
          id={formId}
          className="flex flex-col gap-4"
          onSubmit={async (event) => {
            event.preventDefault()
            if (submitting.current) return
            submitting.current = true
            setSaving(true)
            setError(null)
            try {
              await onContinue(fromFiles)
              onClose()
            } catch (reason) {
              setError(reason instanceof Error ? reason.message : 'Unable to create table.')
            } finally {
              submitting.current = false
              setSaving(false)
            }
          }}
        >
          <fieldset disabled={saving} className="flex flex-col gap-2.5" aria-label="How would you like to start?">
            {[
              {
                value: true,
                label: 'Multi-document analysis',
                description: 'Ask the same questions across multiple documents. Each document becomes a row, and each question becomes a column.',
              },
              {
                value: false,
                label: 'Freeform table',
                description:
                  'Describe the table you need in chat, and Paxton builds it. Add a file in chat to use as a source, or build the table yourself by adding rows and columns.',
              },
            ].map((option) => (
              <label
                key={String(option.value)}
                className={`flex cursor-pointer gap-3 rounded-lg border p-4 ${
                  fromFiles === option.value ? 'border-brand-300 bg-brand-50' : 'border-brand-neutral-200 bg-white'
                }`}
              >
                <input
                  type="radio"
                  name="creation-method"
                  aria-labelledby={`${formId}-${option.value}-title`}
                  aria-describedby={`${formId}-${option.value}-description`}
                  checked={fromFiles === option.value}
                  onChange={() => setFromFiles(option.value)}
                  className="mt-0.5 size-5 shrink-0 accent-brand-600"
                />
                <span className="flex flex-col gap-2">
                  <span id={`${formId}-${option.value}-title`} className="text-base font-medium leading-6 text-brand-neutral-950">
                    {option.label}
                  </span>
                  <span id={`${formId}-${option.value}-description`} className="text-sm leading-5 text-brand-neutral-800">
                    {option.description}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
        </form>
      </ModalContent>
    </Modal>
  )
}
